/**
 * Header navigation interactions.
 * Desktop: hover mega menu + simple dropdown
 * Mobile: hamburger + slide panels
 * Scroll: sticky hide/show
 */

class CHeader {
  constructor() {
    this.header = document.getElementById('c-header');
    if (!this.header) return;

    this.burger = this.header.querySelector('.js-c-header-burger');
    this.overlay = this.header.querySelector('#c-header-mobile-overlay');
    this.megaItems = this.header.querySelectorAll('.c-header__menu-item--mega, .c-header__menu-item--dropdown');

    this._lastScroll = 0;
    this._scrollTicking = false;

    this._bindBurger();
    this._bindDesktopMenu();
    this._bindMobilePanels();
    this._bindScroll();
    this._bindSearch();
    // Mega menu product tiles + banners are loaded via AJAX on first open
    // (see _loadMega / _bindMegaInner) — nothing to bind up-front.
    this._syncHeaderHeight();
    window.addEventListener('resize', () => this._syncHeaderHeight(), { passive: true });
  }

  // ---- Hamburger ----

  _bindBurger() {
    if (!this.burger) return;

    this.burger.addEventListener('click', () => {
      const open = this.header.classList.toggle('is-nav-open');
      this.burger.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.overlay?.setAttribute('aria-hidden', open ? 'false' : 'true');
      document.body.classList.toggle('c-header-nav-open', open);
    });
  }

  _closeNav() {
    this.header.classList.remove('is-nav-open');
    this.burger?.setAttribute('aria-expanded', 'false');
    this.overlay?.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('c-header-nav-open');

    // Reset mobile panels to main
    this._resetMobilePanels();
  }

  // ---- Desktop hover menu ----

  _bindDesktopMenu() {
    this.megaItems.forEach((item) => {
      let hoverTimer = null;

      item.addEventListener('mouseenter', () => {
        if (window.innerWidth < 992) return;
        clearTimeout(hoverTimer);
        this._openItem(item);
      });

      item.addEventListener('mouseleave', () => {
        if (window.innerWidth < 992) return;
        hoverTimer = setTimeout(() => this._closeItem(item), 80);
      });

      // Keep open when hovering over the dropdown itself
      const panel = item.querySelector('.c-header__mega, .c-header__dropdown');
      if (panel) {
        panel.addEventListener('mouseenter', () => clearTimeout(hoverTimer));
        panel.addEventListener('mouseleave', () => {
          if (window.innerWidth < 992) return;
          hoverTimer = setTimeout(() => this._closeItem(item), 80);
        });
      }
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.c-header__menu-item')) {
        this._closeAllItems();
      }
    });

    // Prefetch mega content the first time the pointer enters the nav region,
    // so it's ready before the user reaches a specific item (hides AJAX latency).
    const nav = this.header.querySelector('.c-header__nav');
    if (nav) {
      nav.addEventListener('mouseenter', () => {
        if (window.innerWidth < 992) return;
        this.megaItems.forEach((item) => this._loadMega(item));
      }, { once: true });
    }
  }

  _openItem(item) {
    this._closeAllItems();
    this._closeSearch();
    item.classList.add('is-open');
    this._loadMega(item); // AJAX-defer: fetch product tiles + banners on first open
  }

  _closeItem(item) {
    item.classList.remove('is-open');
  }

  _closeAllItems() {
    this.megaItems.forEach((i) => i.classList.remove('is-open'));
  }

  // ---- Mobile slide panels ----

  _bindMobilePanels() {
    // Open sub-panel
    this.header.querySelectorAll('.js-c-header-sub-open').forEach((btn) => {
      btn.addEventListener('click', () => {
        const target = btn.dataset.target;
        const subPanel = this.overlay?.querySelector(`[data-panel="${target}"]`);
        const mainPanel = this.overlay?.querySelector('.c-header__mobile-panel--main');
        if (!subPanel || !mainPanel) return;

        mainPanel.classList.remove('is-active');
        subPanel.classList.add('is-active');
      });
    });

    // Back to main panel
    this.header.querySelectorAll('.js-c-header-sub-close').forEach((btn) => {
      btn.addEventListener('click', () => this._resetMobilePanels());
    });
  }

  _resetMobilePanels() {
    const panels = this.overlay?.querySelectorAll('.c-header__mobile-panel');
    const mainPanel = this.overlay?.querySelector('.c-header__mobile-panel--main');
    panels?.forEach((p) => p.classList.remove('is-active'));
    mainPanel?.classList.add('is-active');
  }

  // ---- Search expand ----

  _bindSearch() {
    this.searchWrapper = this.header.querySelector('.js-c-header-search');
    if (!this.searchWrapper) return;

    this.searchTrigger = this.searchWrapper.querySelector('.c-header__search-trigger');
    this.searchInput   = this.searchWrapper.querySelector('.c-header__search-input')
                      || this.searchWrapper.querySelector('.dgwt-wcas-search-input');

    this.searchTrigger?.addEventListener('click', () => this._openSearch());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.searchWrapper.classList.contains('is-open')) {
        this._closeSearch();
      }
    });

    document.addEventListener('click', (e) => {
      if (
        this.searchWrapper.classList.contains('is-open') &&
        !e.target.closest('.js-c-header-search')
      ) {
        this._closeSearch();
      }
    });
  }

  _openSearch() {
    this._closeAllItems();
    this.searchWrapper.classList.add('is-open');
    this.searchTrigger?.setAttribute('aria-expanded', 'true');
    setTimeout(() => this.searchInput?.focus(), 50);
  }

  _closeSearch() {
    if (!this.searchWrapper?.classList.contains('is-open')) return;
    this.searchWrapper.classList.remove('is-open');
    this.searchTrigger?.setAttribute('aria-expanded', 'false');
  }

  // ---- Header height sync (for mobile overlay top position) ----

  _syncHeaderHeight() {
    const h = this.header.getBoundingClientRect().height;
    document.documentElement.style.setProperty('--c-header-h', h + 'px');
  }

  // ---- Mega menu: AJAX-defer content load ----

  _loadMega(item) {
    const inner = item.querySelector('.c-header__mega-inner');
    if (!inner) return; // not a mega item (simple dropdown)

    const productsEl = inner.querySelector('.c-header__mega-products[data-mega-nav]');
    if (!productsEl || productsEl.dataset.megaLoaded || productsEl.dataset.megaLoading) return;

    const cfg = window.labifyMegaMenu;
    if (!cfg || !cfg.ajaxurl) return;

    productsEl.dataset.megaLoading = '1';
    const url = `${cfg.ajaxurl}?action=labify_mega_menu&nav=${encodeURIComponent(productsEl.dataset.megaNav)}`;

    fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'fetch' } })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((res) => {
        if (!res || !res.success) throw new Error('bad response');
        productsEl.innerHTML = res.data.products || '';
        if (res.data.banners) {
          productsEl.insertAdjacentHTML('afterend', res.data.banners);
        }
        // Some category links were tagged data-products-set on intent, but their
        // set resolved to 0 products (no matching [data-set]). Drop the attribute
        // so they behave as plain links instead of forcing a switch to default.
        const validSets = new Set(
          [...productsEl.querySelectorAll('[data-set]')].map((s) => s.dataset.set)
        );
        inner.querySelectorAll('[data-products-set]').forEach((l) => {
          if (!validSets.has(l.dataset.productsSet)) l.removeAttribute('data-products-set');
        });
        productsEl.dataset.megaLoaded = '1';
        delete productsEl.dataset.megaLoading;
        this._bindMegaInner(inner);
      })
      .catch(() => {
        // Allow a retry on the next open.
        delete productsEl.dataset.megaLoading;
      });
  }

  // ---- Mega menu product sets + link-col + banner hover (bound after load) ----

  _bindMegaInner(inner) {
    if (!inner || inner.dataset.megaBound) return;
    inner.dataset.megaBound = '1';

    {
      const productsEl = inner.querySelector('.c-header__mega-products');
      const hoverCols  = inner.querySelectorAll('.c-header__mega-hover-col');
      const bannerEls  = inner.querySelectorAll('.c-header__mega-banner');

      if (!productsEl && !hoverCols.length && !bannerEls.length) return;

      const sets       = productsEl ? productsEl.querySelectorAll('.c-header__mega-products-set') : [];
      const defaultSet = productsEl ? productsEl.querySelector('[data-set="default"]') : null;

      const hideHoverCols = () => hoverCols.forEach((c) => c.classList.remove('is-active'));
      const hideBanners   = () => bannerEls.forEach((b) => b.classList.remove('is-visible'));
      const showProducts  = () => productsEl?.classList.remove('is-products-hidden');
      const hideProducts  = () => productsEl?.classList.add('is-products-hidden');

      const activateSet = (key) => {
        sets.forEach((s) => s.classList.remove('is-active'));
        const target = productsEl?.querySelector(`[data-set="${key}"]`);
        (target || defaultSet)?.classList.add('is-active');
      };

      const restoreDefault = () => {
        hideHoverCols();
        hideBanners();
        showProducts();
        sets.forEach((s) => s.classList.remove('is-active'));
        defaultSet?.classList.add('is-active');
      };

      // Hover: product set link
      inner.querySelectorAll('[data-products-set]').forEach((link) => {
        link.addEventListener('mouseenter', () => {
          hideHoverCols();
          hideBanners();
          showProducts();
          activateSet(link.dataset.productsSet);
        });
      });

      // Hover: link-col link (link_type='list') → hide products, show col
      inner.querySelectorAll('[data-link-col]').forEach((link) => {
        link.addEventListener('mouseenter', () => {
          hideHoverCols();
          hideBanners();
          hideProducts();
          const targetCol = inner.querySelector(`[data-col-id="${link.dataset.linkCol}"]`);
          targetCol?.classList.add('is-active');
        });
      });

      // Hover: banner link (link_type='banner') → hide products + cols, show banner
      inner.querySelectorAll('[data-banner-set]').forEach((link) => {
        link.addEventListener('mouseenter', () => {
          if (window.innerWidth < 992) return;
          hideHoverCols();
          hideBanners();
          hideProducts();
          const bannerEl = inner.querySelector(`.c-header__mega-banner[data-banner-id="${link.dataset.bannerSet}"]`);
          bannerEl?.classList.add('is-visible');
        });
      });

      inner.addEventListener('mouseleave', restoreDefault);
    }
  }

  // ---- Scroll hide/show ----

  _bindScroll() {
    window.addEventListener('scroll', () => {
      if (this._scrollTicking) return;
      this._scrollTicking = true;
      requestAnimationFrame(() => {
        const current = window.scrollY;
        // Only hide after scrolled past header height
        if (current > 120) {
          if (current > this._lastScroll) {
            this.header.classList.add('is-hidden');
            this._closeAllItems();
            this._closeNav();
          } else {
            this.header.classList.remove('is-hidden');
          }
        } else {
          this.header.classList.remove('is-hidden');
        }
        this._lastScroll = Math.max(0, current);
        this._scrollTicking = false;
      });
    }, { passive: true });
  }
}

document.addEventListener('DOMContentLoaded', () => new CHeader());
